import React, { FC, ReactNode } from 'react';
import { Typography, makeStyles } from '@material-ui/core';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import clsx from 'clsx';
import moment, { Moment } from 'moment';
import type { Theme } from 'src/theme';
import { wallTimeToInstant, zoneAbbreviation } from 'src/features/timezone/utils/datetime';
import { displayDateTimeFormat } from 'src/utils/dates';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'inline-flex',
    alignItems: 'flex-start'
  },
  picker: {
    width: 280
  },
  pickerWithZone: {
    '& .MuiOutlinedInput-root': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0
    }
  },
  zone: {
    display: 'flex',
    alignItems: 'center',
    height: 56,
    padding: theme.spacing(0, 1.5),
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: 0,
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    whiteSpace: 'nowrap'
  }
}));

interface ZonedDateTimePickerProps {
  label: string;
  value: string;
  zone: string;
  onChange: (_value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
}

const ZonedDateTimePicker: FC<ZonedDateTimePickerProps> = ({
  label,
  value,
  zone,
  onChange,
  onBlur,
  disabled = false,
  error = false,
  helperText
}) => {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <KeyboardDateTimePicker
        className={clsx(classes.picker, zone && classes.pickerWithZone)}
        clearable
        disabled={disabled || !zone}
        error={error}
        format={displayDateTimeFormat}
        helperText={helperText}
        inputVariant="outlined"
        label={label}
        onBlur={onBlur}
        onChange={(next: Moment | null) =>
          onChange(next && next.isValid() ? next.format('YYYY-MM-DDTHH:mm') : '')
        }
        value={value ? moment(value) : null}
      />
      {zone && (
        <div className={classes.zone}>
          <Typography color="textSecondary" variant="body2">
            {zoneAbbreviation(zone, value ? wallTimeToInstant(value, zone) : undefined)}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default ZonedDateTimePicker;
